import * as m from '@staiter/i18n';
import { useQuery } from '@tanstack/react-query';
// S6 — Writeoff workspace (per Work Order).
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { EmptyState, PageHeader, Table } from '~/components/data';
import { HeroMetric } from '~/components/domain';
import { Button, Select } from '~/components/primitives';
import { ProvenanceChip } from '~/components/status';
import { fetchWorkOrders } from '~/lib/api/work-orders';
import { fetchWriteoffWorkspace } from '~/lib/api/workspaces';
import shared from './_auth._shared.module.css';
import styles from './_auth.writeoff.module.css';

export const Route = createFileRoute('/_auth/writeoff')({
  staticData: { minMainWidth: 960 },
  component: WriteoffWorkspace,
});

function WriteoffWorkspace(): JSX.Element {
  const [workOrderId, setWorkOrderId] = useState<string>('');

  const wos = useQuery({
    queryKey: ['work-orders', 'for-writeoff'],
    queryFn: () => fetchWorkOrders({}),
  });
  const workspace = useQuery({
    queryKey: ['writeoff', 'workspace', workOrderId],
    queryFn: () => fetchWriteoffWorkspace(workOrderId),
    enabled: !!workOrderId,
    staleTime: 15_000,
  });

  const options = (wos.data?.items ?? []).map((w) => ({
    value: w.id,
    label: `${w.code} · ${w.state}`,
  }));

  const rows = workspace.data?.rows ?? [];

  return (
    <div className={shared.page}>
      <PageHeader title={m.writeoff_page_title()} description={m.writeoff_page_description()} />

      <div className={styles.selectorRow}>
        <Select
          label={m.writeoff_wo_label()}
          value={workOrderId}
          onChange={(e) => setWorkOrderId(e.target.value)}
          options={[{ value: '', label: m.writeoff_wo_placeholder() }, ...options]}
        />
        {workspace.data && (
          <div className={styles.meta}>
            <span className={shared.mono}>{workspace.data.work_order_label}</span>
            <span className={shared.subtle}>{workspace.data.status}</span>
          </div>
        )}
      </div>

      {!workOrderId && (
        // FE-118: empty state с primary CTA — выбрать WO через scope picker.
        <EmptyState
          title={m.writeoff_empty_title()}
          description={m.writeoff_empty_desc()}
          action={
            <Button
              variant="secondary"
              onClick={() => {
                const el = document.querySelector<HTMLInputElement>(
                  'input[placeholder*="WO"],input[name*="work_order"]',
                );
                el?.focus();
              }}
            >
              {m.ui_vybrat_zakaz()}
            </Button>
          }
        />
      )}

      {workOrderId && workspace.data && (
        <>
          <div className={styles.heroRow}>
            <HeroMetric label={m.writeoff_hero_rows()} value={rows.length} />
            <HeroMetric
              label={m.writeoff_hero_variance()}
              value={workspace.data.total_variance_value ?? '—'}
            />
            <HeroMetric
              label={m.writeoff_hero_with_reason()}
              value={rows.filter((r) => r.variance_reason !== null).length}
            />
          </div>

          <Table
            ariaLabel={m.writeoff_table_aria()}
            loading={workspace.isLoading}
            data={rows}
            rowKey={(r) => r.item_id}
            emptyTitle={m.writeoff_no_rows()}
            columns={[
              {
                id: 'item',
                header: m.writeoff_col_item(),
                accessor: (r) => r.item_label,
                mono: true,
              },
              {
                id: 'req',
                header: m.writeoff_col_required(),
                accessor: (r) => `${r.required_qty} ${r.uom}`,
                align: 'right',
                mono: true,
              },
              {
                id: 'alloc',
                header: m.writeoff_col_allocated(),
                accessor: (r) => `${r.allocated_qty} ${r.uom}`,
                align: 'right',
                mono: true,
              },
              {
                id: 'var',
                header: m.writeoff_col_variance(),
                accessor: (r) => `${r.variance_qty} ${r.uom}`,
                align: 'right',
                mono: true,
              },
              {
                id: 'reason',
                header: m.writeoff_col_reason(),
                accessor: (r) => r.variance_reason ?? '—',
                mono: true,
              },
              {
                id: 'batch',
                header: m.writeoff_col_source(),
                accessor: (r) =>
                  r.source_batch ? (
                    <ProvenanceChip source={r.source_system} document_id={r.source_batch} />
                  ) : (
                    '—'
                  ),
              },
            ]}
          />

          <div className={styles.legend}>
            <h3 className={shared.sectionTitle}>{m.writeoff_legend_title()}</h3>
            <ul className={styles.legendList}>
              <li>{m.writeoff_legend_normative()}</li>
              <li>{m.writeoff_legend_scrap()}</li>
              <li>{m.writeoff_legend_error()}</li>
              <li>{m.writeoff_legend_shortage()}</li>
              <li>{m.writeoff_legend_spoilage()}</li>
              <li>{m.writeoff_legend_other()}</li>
            </ul>
          </div>
        </>
      )}
    </div>
  );
}
